import { useMemo } from 'react';
import { HSOC_V2_SCREEN, TRACKING_SOURCES } from '../../utils/tracking';
import { usePlanQuizData } from './PlanQuizDataContext';
import { PlanQuizStep, QUIZ_USE_CASE_MAP } from './planQuizConstants';
const SOURCE = TRACKING_SOURCES.PLAN_QUIZ_MODAL;
const SCREEN = HSOC_V2_SCREEN;
const HELP_ME_CHOOSE_IMPRESSION_SESSION_KEY = 'pricing-pages.help-me-choose-impression-tracked';
function buildAnswers(ctx) {
  /* While step is EMAIL_INPUT, emailOrLink is either empty or whatever the
     user has typed so far and hasn't submitted — including a raw email
     address before EmailLinkInput's cleanDomain() strips it down to a
     domain. Excluding it here until the user has moved past that step
     avoids leaking unsubmitted, uncleaned input (e.g. via a modal-close
     event) into tracking. */
  const parts = [ctx.step !== PlanQuizStep.EMAIL_INPUT && ctx.emailOrLink ? `domain:${ctx.emailOrLink}` : null, ctx.selectedUseCases.length > 0 ? `useCases:${ctx.selectedUseCases.map(uc => QUIZ_USE_CASE_MAP[uc]).join(',')}` : null, ctx.teamSize ? `teamSize:${ctx.teamSize}` : null].filter(Boolean);
  return parts.length > 0 ? parts.join('|') : undefined;
}
/**
 * Binds the quiz's events to the injected low-level `track` callback. Pure and
 * React-free so the event payloads can be unit-tested by passing a spy; the
 * usePlanQuizTracking hook wires the real one from context.
 */
export function createPlanQuizTracking(track) {
  return {
    /*
     * Fires a Section Viewed impression for the "Help me choose" button,
     * deduplicated to once per session via sessionStorage. The button only
     * renders for the public HSoC page, so an in-app occurrence signals the
     * button leaking into the authed app — a bug worth surfacing.
     */
    trackHelpMeChooseImpression() {
      try {
        if (sessionStorage.getItem(HELP_ME_CHOOSE_IMPRESSION_SESSION_KEY) === '1') {
          return;
        }
        sessionStorage.setItem(HELP_ME_CHOOSE_IMPRESSION_SESSION_KEY, '1');
      } catch (_unused) {
        /*
         * sessionStorage can be unavailable (private mode / blocked storage).
         * The per-mount guard in the component still prevents duplicate fires
         * while the button stays mounted; we accept losing cross-reload dedup.
         */
      }
      track('Section Viewed', {
        action: 'scrolled in view',
        name: TRACKING_SOURCES.HELP_ME_CHOOSE,
        screen: SCREEN,
        trackingSource: TRACKING_SOURCES.HELP_ME_CHOOSE
      });
    },
    trackQuizStepViewed(ctx) {
      var _ctx$flow;
      track('Modal Interaction', {
        action: 'viewed',
        name: SOURCE,
        screen: SCREEN,
        trackingSource: SOURCE,
        quizId: ctx.step,
        flow: (_ctx$flow = ctx.flow) !== null && _ctx$flow !== void 0 ? _ctx$flow : undefined,
        answers: buildAnswers(ctx)
      });
    },
    trackQuizModalOpen(ctx) {
      var _ctx$flow2;
      track('Button Click', {
        action: 'open plan quiz',
        trackingSource: SOURCE,
        screen: SCREEN,
        quizId: ctx.step,
        flow: (_ctx$flow2 = ctx.flow) !== null && _ctx$flow2 !== void 0 ? _ctx$flow2 : undefined,
        answers: buildAnswers(ctx)
      });
    },
    trackQuizModalClose(ctx) {
      var _ctx$flow3;
      track('Modal Interaction', {
        action: 'click to close',
        name: SOURCE,
        screen: SCREEN,
        trackingSource: SOURCE,
        quizId: ctx.step,
        flow: (_ctx$flow3 = ctx.flow) !== null && _ctx$flow3 !== void 0 ? _ctx$flow3 : undefined,
        answers: buildAnswers(ctx)
      });
    },
    trackQuizFlowSelected(flow) {
      track('Button Click', {
        action: 'selected quiz flow',
        selected: flow,
        trackingSource: SOURCE,
        screen: SCREEN
      });
    },
    trackQuizDomainSubmitted(domain) {
      track('Button Click', {
        action: 'submitted domain',
        selected: domain,
        trackingSource: SOURCE,
        screen: SCREEN
      });
    },
    trackQuizUseCasesContinued(useCases) {
      track('Button Click', {
        action: 'completed use cases step',
        selected: useCases.map(uc => QUIZ_USE_CASE_MAP[uc]).join(','),
        trackingSource: SOURCE,
        screen: SCREEN
      });
    },
    trackQuizTeamSizeSelected(size) {
      track('Button Click', {
        action: 'selected team size',
        selected: size,
        trackingSource: SOURCE,
        screen: SCREEN
      });
    },
    trackQuizRecommendationReceived(ctx, details) {
      var _ctx$flow4, _ctx$flow5;
      track('Pageview', {
        screen: SCREEN,
        subscreen: 'plan-quiz-results',
        flow: (_ctx$flow4 = ctx.flow) !== null && _ctx$flow4 !== void 0 ? _ctx$flow4 : undefined
      });
      track('Button Click', {
        action: 'recommendation received',
        tier: details.tier,
        numberOfCoreSeatsInputted: details.seatCount,
        agentCount: details.agentCount,
        pollCount: details.pollCount,
        trackingSource: SOURCE,
        screen: SCREEN,
        quizId: ctx.step,
        flow: (_ctx$flow5 = ctx.flow) !== null && _ctx$flow5 !== void 0 ? _ctx$flow5 : undefined,
        answers: buildAnswers(ctx),
        pollDurationMs: details.pollDurationMs,
        hasCompanyName: details.hasCompanyName,
        companyTagCount: details.companyTagCount,
        companyEmployeeCount: details.companyEmployeeCount,
        isAgentRec: details.isAgentRec,
        recSourceType: details.recSourceType
      });
    },
    trackQuizFeedback(feedback, reason) {
      track('Button Click', {
        action: feedback === 'APPROVED' ? 'upvoted recommendation' : 'downvoted recommendation',
        selected: reason,
        trackingSource: SOURCE,
        screen: SCREEN
      });
    },
    trackQuizStartOver(ctx) {
      var _ctx$flow6;
      track('Button Click', {
        action: 'start over',
        trackingSource: SOURCE,
        screen: SCREEN,
        flow: (_ctx$flow6 = ctx.flow) !== null && _ctx$flow6 !== void 0 ? _ctx$flow6 : undefined,
        answers: buildAnswers(ctx)
      });
    },
    trackQuizRecommendationFailed(ctx, details) {
      var _ctx$flow7;
      track('Button Click', {
        action: 'recommendation failed',
        value: details.error,
        trackingSource: SOURCE,
        screen: SCREEN,
        quizId: ctx.step,
        flow: (_ctx$flow7 = ctx.flow) !== null && _ctx$flow7 !== void 0 ? _ctx$flow7 : undefined,
        answers: buildAnswers(ctx)
      });
    }
  };
}

/**
 * The quiz's tracking API, bound to the consumer-injected `track` callback.
 * Extract-a-hook per TEAM_STYLE for a component that fires many events. The
 * returned object is stable while the callback is (the page memoizes it), so
 * it's safe to reference from effects without widening dep arrays.
 */
export function usePlanQuizTracking() {
  const {
    track
  } = usePlanQuizData();
  return useMemo(() => createPlanQuizTracking(track), [track]);
}